import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Table, Switch, Modal, Tooltip, message, Spin, Descriptions } from 'antd';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { LeftOutlined, DownloadOutlined } from '@ant-design/icons';
import VoterListModalModal from './VoterListModalModal';
import CertificateFieldsModal from './CertificateFieldsModal';
import formatDateInTable from '../../../rewardsAndRecognitionHelper/CommonDate';
import { isDateLessThanOrEqualToCurrent } from '../../utils.js';
import { downloadCSVFile } from '../../../rewardsAndRecognitionHelper/utils.js';
import { fetchFormsByConnectionId } from 'src/_shared/services/utils.js';
import './style.scss';
import NoDataState from '../../../RnRCommonComponent/noDataState/NoDataState.jsx';
import { datediff } from 'src/nominatedRewards/utils.js';

const resolveFormAnswer = (elementId, value, formElements) => {
	const element = formElements.find((el) => el.id === elementId);
	const label = element?.label || 'Missing Form Question';

	if (element?.type === 'select' && element?.options) {
		const option = element.options.find((opt) => opt.value === value);
		return { label, value: option?.label || value };
	}

	return { label, value: value ?? '' };
};

const SelectWinnerPresentation = ({
	selectWinner: nomineeArray,
	nominationConfig,
	loading,
	selectWinnerFunc,
	nomineeVoterList,
	getNomineeVoterListById,
	handleOpenViewReward,
	rewardDetails,
	client,
}) => {
	const history = useHistory();
	const currentUser = useSelector((state) => state?.user?.currentUser);

	const pastEndDate =
		datediff(new Date(), new Date(nominationConfig.endDate)) <= 0;

	const [selectedWinnerArray, setSelectedWinnerArray] = useState([]);
	const [customFormDefinition, setCustomFormDefinition] = useState(null);
	const [formAnswersModal, setFormAnswersModal] = useState({ visible: false, answers: [] });
	const hasCustomForm = Boolean(nominationConfig?.customFormId);

	useEffect(() => {
		if (!hasCustomForm || !currentUser?.companyId || !client) return;
		const loadForm = async () => {
			try {
				const forms = await fetchFormsByConnectionId(currentUser, 'nomination_form', client);
				const matched = forms.find((f) => f?.metadata?.id === nominationConfig.customFormId);
				setCustomFormDefinition(matched || null);
			} catch {
				setCustomFormDefinition(null);
			}
		};
		loadForm();
	}, [hasCustomForm, nominationConfig?.customFormId, currentUser?.companyId]);

	const [showVoterListModal, setShowVoterListModal] = useState({
		show: false,
		voterList: [],
	});
	const [showCertificateModal, setShowCertificateModal] = useState(false);
	const [pendingWinners, setPendingWinners] = useState([]);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const processedNomineeData = useMemo(() => {
		if (!nomineeArray || !Array.isArray(nomineeArray)) return [];

		const nomineeMap = new Map();

		nomineeArray.forEach((nomination) => {
			const nomineeId = nomination.nomineeId;
			const parsedAnswers = nomination.customFormAnswers
				? (() => { try { return JSON.parse(nomination.customFormAnswers); } catch { return null; } })()
				: null;

			if (nomineeMap.has(nomineeId)) {
				const existing = nomineeMap.get(nomineeId);
				existing.votes += 1;
				if (new Date(nomination.createdAt) < new Date(existing.dateNominated)) {
					existing.id = nomination.id;
					existing.nominationReason = nomination.nominationReason;
					existing.dateNominated = nomination.createdAt;
					existing.nominator = nomination.nominator;
				}
				if (nomination.isWinner) {
					existing.isWinner = true;
				}
				if (parsedAnswers) {
					existing.allCustomFormAnswers.push({
						nominator: nomination.nominator,
						answers: parsedAnswers,
					});
				}
			} else {
				nomineeMap.set(nomineeId, {
					id: nomination.id,
					nomineeId: nomineeId,
					nominee: nomination.nominee,
					nominator: nomination.nominator,
					votes: 1,
					nominationReason: nomination.nominationReason,
					dateNominated: nomination.createdAt,
					isWinner: nomination.isWinner || false,
					allCustomFormAnswers: parsedAnswers
						? [{ nominator: nomination.nominator, answers: parsedAnswers }]
						: [],
				});
			}
		});

		return Array.from(nomineeMap.values());
	}, [nomineeArray]);

	const generateNominationCSV = useCallback(() => {
		if (!nomineeArray || nomineeArray.length === 0) {
			message.error('No data available to export');
			return;
		}

		const headers = [
			'Nominee Name',
			'Date Nominated',
			'Nominator Name',
			'Number of Nominations',
			'Nomination Reason',
			'Is Winner',
		];

		const nomineeCountMap = new Map();
		const nomineeWinnerMap = new Map();

		nomineeArray.forEach((nomination) => {
			const count = nomineeCountMap.get(nomination.nomineeId) || 0;
			nomineeCountMap.set(nomination.nomineeId, count + 1);
			if (nomination.isWinner) {
				nomineeWinnerMap.set(nomination.nomineeId, true);
			}
		});

		const sortedNominations = [...nomineeArray].sort((a, b) => {
			if (a.nomineeId !== b.nomineeId) {
				return a.nomineeId.localeCompare(b.nomineeId);
			}
			return new Date(a.createdAt) - new Date(b.createdAt);
		});

		const nomineeIndexMap = new Map();

		const csvData = sortedNominations.map((nomination) => {
			const nomineeName = nomination.nominee
				? `${nomination.nominee.firstName || ''} ${nomination.nominee.lastName || ''}`.trim()
				: '';
			const nominatorName = nomination.nominator
				? `${nomination.nominator.firstName || ''} ${nomination.nominator.lastName || ''}`.trim()
				: '';

			const currentIndex = (nomineeIndexMap.get(nomination.nomineeId) || 0) + 1;
			nomineeIndexMap.set(nomination.nomineeId, currentIndex);

			const totalCount = nomineeCountMap.get(nomination.nomineeId);
			const nominationCountText =
				totalCount > 1 ? `${currentIndex} of ${totalCount}` : totalCount;

			const isWinner = nomineeWinnerMap.get(nomination.nomineeId) || false;

			return [
				nomineeName,
				formatDateInTable(nomination.createdAt),
				nominatorName,
				nominationCountText,
				nomination.nominationReason || '',
				isWinner ? 'Yes' : 'No',
			];
		});

		const csvContent = [headers, ...csvData]
			.map((row) => row.map((field) => `"${field}"`).join(','))
			.join('\n');

		downloadCSVFile(
			csvContent,
			`${rewardDetails?.name || 'Nomination'}_Export`
		);
	}, [nomineeArray, rewardDetails?.name]);
	const openFormAnswersModal = useCallback((allCustomFormAnswers) => {
		const formElements = customFormDefinition?.elements || [];
		const resolved = allCustomFormAnswers.map(({ nominator, answers }) => {
			const nominatorName = nominator
				? `${nominator.firstName || ''} ${nominator.lastName || ''}`.trim()
				: 'Unknown';
			const fields = Object.entries(answers).map(([key, value]) =>
				resolveFormAnswer(key, value, formElements)
			);
			return { nominatorName, fields };
		});
		setFormAnswersModal({ visible: true, answers: resolved });
	}, [customFormDefinition]);

	const columns = [
		{
			title: 'Nominee',
			dataIndex: 'nominee',
			key: 'nominee',
			render: (nominee) => {
				const nomineeName = nominee
					? `${nominee.firstName || ''} ${nominee.lastName || ''}`.trim()
					: '';
				return (
					<span
						className={`status-${nomineeName?.toLowerCase().replace(/\s+/g, '-') || ''}`}
					>
						{nomineeName}
					</span>
				);
			},
		},
		{
			title: 'Date Nominated',
			dataIndex: 'dateNominated',
			key: 'dateNominated',
			sorter: (a, b) => new Date(a.dateNominated) - new Date(b.dateNominated),
			render: (dateNominated) => {
				return <span>{formatDateInTable(dateNominated)}</span>;
			},
		},
		{
			title: 'Nominator',
			dataIndex: 'nominator',
			key: 'nominator',
			render: (nominator) => {
				const nominatorName = nominator
					? `${nominator.firstName || ''} ${nominator.lastName || ''}`.trim()
					: '';
				return (
					<span
						className={`status-${nominatorName?.toLowerCase().replace(/\s+/g, '-') || ''}`}
					>
						{nominatorName}
					</span>
				);
			},
		},
		{
			title: 'Nominations',
			dataIndex: 'votes',
			key: 'votes',
			sorter: (a, b) => a.votes - b.votes,
			render: (votes, rawContent) => {
				return true ? (
					<Button
						type="link"
						onClick={() => {
							setShowVoterListModal({
								show: true,
								nomineeId: rawContent.nomineeId,
							});
						}}
					>
						{votes}
					</Button>
				) : (
					<span className={``}>None</span>
				);
			},
		},
		...(hasCustomForm
			? [
					{
						title: 'Form Answers',
						dataIndex: 'allCustomFormAnswers',
						key: 'formAnswers',
						render: (allCustomFormAnswers) => {
							if (!allCustomFormAnswers?.length) return <span>—</span>;
							return (
								<Button
									type="link"
									onClick={() => openFormAnswersModal(allCustomFormAnswers)}
								>
									{allCustomFormAnswers.length > 1 ? 'View All' : 'View'}
								</Button>
							);
						},
					},
				]
			: [
					{
						title: 'Nomination Reason(s)',
						dataIndex: 'nominationReason',
						key: 'nominationReason',
						render: (nominationReason, rawContent) => {
							return rawContent?.votes > 1 ? (
								<Button
									type="link"
									onClick={() => {
										setShowVoterListModal({
											show: true,
											nomineeId: rawContent.nomineeId,
										});
									}}
								>
									View All
								</Button>
							) : (
								<span
									style={{
										wordWrap: 'break-word',
										fontSize: '14px',
										lineHeight: '18px',
										fontWeight: 600,
									}}
								>
									{nominationReason || 'None'}
								</span>
							);
						},
					},
				]),
		{
			title: 'Winner',
			dataIndex: 'id',
			key: 'id',
			render: (id, rawContent) => {
				let temp = selectedWinnerArray;
				const currentSelected = temp.includes(id);
				const isWinner = rawContent.isWinner;
				const reasonsToDisableSwitch = [
					!pastEndDate && nominationConfig?.status === 'Open',
					isWinner,
					!currentSelected &&
						selectedWinnerArray?.length === nominationConfig?.awardedCount,
					nominationConfig?.status === 'Complete',
				];
				const disableSwitch = reasonsToDisableSwitch?.includes(true);

				return (
					<>
						<Tooltip
							title={
								!pastEndDate && nominationConfig?.status === 'Open'
									? 'Nomination period is still open, winners can not be selected until after the period closes.'
									: ''
							}
						>
							<Switch
								className={currentSelected ? 'action-undo' : 'action-select'}
								onChange={() => {
									if (currentSelected) {
										temp = temp.filter((temp_id) => temp_id != id);
									} else {
										temp.push(id);
									}
									setSelectedWinnerArray([...temp]);
								}}
								checked={
									nominationConfig?.status === 'Submitted'
										? rawContent?.isWinner
										: selectedWinnerArray?.includes(id)
								}
								disabled={disableSwitch ? disableSelection : false}
								type={currentSelected ? 'primary' : 'default'}
							>
								Select
							</Switch>
						</Tooltip>
					</>
				);
			},
		},
	];

	const nomineeCount = useMemo(() => {
		return nominationConfig ? nominationConfig?.awardedCount : '-';
	}, [nominationConfig]);

	const disableSelection = useMemo(() => {
		return (
			selectedWinnerArray?.length === nomineeCount ||
			nominationConfig?.status !== 'Complete' ||
			!isDateLessThanOrEqualToCurrent(nominationConfig?.endDate)
		);
	}, [nomineeCount, selectedWinnerArray?.length, nominationConfig]);

	const showSubmitButton = useMemo(() => {
		return (
			(processedNomineeData?.length &&
				selectedWinnerArray?.length === processedNomineeData?.length) ||
			(selectedWinnerArray?.length &&
				selectedWinnerArray?.length <= nomineeCount)
		);
	}, [nomineeCount, selectedWinnerArray?.length, processedNomineeData]);

	const confirm = useCallback(() => {
		Modal.confirm({
			title: 'Confirm Winner Selection',
			content: (
				<div>
					Winners for '{rewardDetails?.name}' have been selected. Are you sure
					you want to proceed?
					<br />{' '}
					<div style={{ fontStyle: 'italic' }}>
						Please note that changes cannot be made after submission.
					</div>
				</div>
			),
			okText: 'Proceed',
			cancelText: 'Cancel',
			onOk: () => {
				const winners = processedNomineeData.filter((nomination) =>
					selectedWinnerArray?.includes(nomination.id)
				);
				const hasCertificate =
					nominationConfig?.badge?.certificateConfig?.key;
				if (hasCertificate) {
					setPendingWinners(winners);
					setShowCertificateModal(true);
				} else {
					selectWinnerFunc(winners, nominationConfig);
				}
			},
		});
	}, [rewardDetails?.name, selectedWinnerArray?.length]);

	const confirmLessSelcted = useCallback(() => {
		Modal.confirm({
			title: 'Confirm Submission',
			content: `Are you sure you want to submit without selecting the appropriate number of winners? You will not be able to make changes after submission.`,
			okText: 'Submit',
			cancelText: 'Cancel',
			onOk: () => {
				confirm();
			},
		});
	}, [rewardDetails?.name, selectedWinnerArray?.length]);

	useEffect(() => {
		nominationConfig?.badge?.id &&
			handleOpenViewReward(nominationConfig?.badge?.id);
	}, [nominationConfig?.badge?.id]);

	useEffect(() => {
		if (processedNomineeData?.length > 0) {
			const existingWinners = processedNomineeData
				.filter((nominee) => nominee.isWinner)
				.map((nominee) => nominee.id);
			setSelectedWinnerArray(existingWinners);
		}
	}, [processedNomineeData]);

	return (
		<>
			{isSubmitting && (
				<div
					style={{
						position: 'fixed',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						background: 'rgba(255, 255, 255, 0.7)',
						zIndex: 1000,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<Spin size="large" tip="Creating assignments and generating certificates..." />
				</div>
			)}
			<div className="main select-winner-presentation">
				<div className={'bread-crubs'}>
					<Button
						type="link"
						onClick={() => {
							history.push('/nominations');
						}}
					>
						<LeftOutlined className="bread-crubs-text" />
						<span className="bread-crubs-text">Nomination Periods</span>
					</Button>
				</div>
				<div className="page-title">
					<div className="d-flex">
						<h2 className="page-heading">{rewardDetails?.name}</h2>
						{nominationConfig?.status !== 'Submitted' ? (
							<Button type="link">
								{`${selectedWinnerArray?.length || 0} / ${nomineeCount} Winners Chosen`}
							</Button>
						) : (
							<></>
						)}
					</div>
					<div className="d-flex">
						<Button
							type="link"
							disabled={
								nominationConfig?.status !== 'Complete' ||
								!isDateLessThanOrEqualToCurrent(nominationConfig?.endDate)
							}
							onClick={() => {
								generateNominationCSV();
							}}
							className="btn-link"
						>
							<i className="icon-export btn-large" />
							<span className="page-heading-left">Export CSV</span>
						</Button>
					</div>
				</div>
			</div>
			<div className="nomination-rewards-table-container">
				<div className="nomination-rewards-table table-card">
					<Table
						rowKey={(record) => record.nomineeId}
						dataSource={processedNomineeData}
						pagination={false}
						loading={loading}
						columns={columns}
						locale={{
							emptyText: <NoDataState title={'No nominees available.'} />,
						}}
					/>
				</div>
				{showSubmitButton && nominationConfig.status !== 'Complete' ? (
					<div className="p-3 d-flex jc-end align-items-center">
						<Button
							type="primary"
							onClick={() => {
								selectedWinnerArray?.length < nomineeCount
									? confirmLessSelcted()
									: confirm();
							}}
						>
							<span className="bread-crubs-text">Submit</span>
						</Button>
						{nominationConfig?.badge?.certificateConfig?.key && (
							<span
								style={{
									marginLeft: 12,
									fontSize: 13,
									color: '#faad14',
									fontStyle: 'italic',
								}}
							>
								Requires additional certificate information
							</span>
						)}
					</div>
				) : (
					<></>
				)}

				{showVoterListModal.show ? (
					<VoterListModalModal
						showModal={showVoterListModal.show}
						setShowModal={setShowVoterListModal}
						nomineeVoterList={nomineeVoterList}
						nominationConfigId={nominationConfig.id}
						getNomineeVoterListById={getNomineeVoterListById}
						nomineeId={showVoterListModal.nomineeId}
					/>
				) : (
					<></>
				)}
			</div>
			{showCertificateModal && (
				<CertificateFieldsModal
					visible={showCertificateModal}
					onCancel={() => {
						setShowCertificateModal(false);
						setPendingWinners([]);
					}}
					onSubmit={async (fieldValuesMap) => {
						setShowCertificateModal(false);
						setIsSubmitting(true);
						try {
							await selectWinnerFunc(
								pendingWinners,
								nominationConfig,
								fieldValuesMap
							);
						} finally {
							setIsSubmitting(false);
						}
					}}
					winners={pendingWinners}
					certificateConfig={nominationConfig?.badge?.certificateConfig}
				/>
			)}
			<Modal
				open={formAnswersModal.visible}
				title="Custom Form Answers"
				footer={null}
				onCancel={() => setFormAnswersModal({ visible: false, answers: [] })}
				width={600}
			>
				{formAnswersModal.answers.map((entry, index) => (
					<div key={index} style={formAnswersModal.answers.length > 1 ? { marginBottom: 24 } : undefined}>
						{formAnswersModal.answers.length > 1 && (
							<div style={{ fontWeight: 700, marginBottom: 8 }}>
								Nominated by: {entry.nominatorName}
							</div>
						)}
						<Descriptions column={1} bordered size="small">
							{entry.fields.map((field, i) => (
								<Descriptions.Item key={i} label={field.label}>
									{field.value || '—'}
								</Descriptions.Item>
							))}
						</Descriptions>
					</div>
				))}
			</Modal>
		</>
	);
};

export default SelectWinnerPresentation;
